import {
  Box,
  Button,
  ButtonProps,
  FormField,
  IconProps,
  NonCancelableCustomEvent,
  SpaceBetween,
  Textarea,
  TextareaProps,
} from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import constants from '../../common/constants';
import '../../resources/styles/scss/components/comment/CommentForm.css';

interface Props {
  onComment: (commentText: string) => Promise<void>;
  buttonText?: string;
  formPlaceholder?: string;
  formLabel?: string;
  formDescription?: string;
  buttonVariant?: ButtonProps.Variant;
  buttonIcon?: IconProps.Name;
  visible?: boolean;
}

const SendCommentForm = (props: Props) => {
  const {
    onComment,
    buttonText,
    formLabel,
    formDescription,
    formPlaceholder,
    buttonVariant,
    buttonIcon,
    visible = true,
  } = props;
  const [commentText, setCommentText] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isCreatingComment, setIsCreatingComment] = useState(false);

  const onCommentTextChanged = (event: NonCancelableCustomEvent<TextareaProps.ChangeDetail>) => {
    if (event.detail.value.length > constants.COMMENTS_LENGTH_LIMIT) {
      event.detail.value = event.detail.value.slice(0, constants.COMMENTS_LENGTH_LIMIT - 1);
    }
    setCommentText(event.detail.value);
    setValidationError('');
  };

  const addComment = async () => {
    if (validate()) {
      setIsCreatingComment(true);
      if (onComment) {
        try {
          await onComment(commentText);
        } finally {
          setCommentText('');
          setIsCreatingComment(false);
        }
      }
    }
  };

  const validate = () => {
    if (!commentText) {
      setValidationError(constants.REQUIRED);
      return false;
    }
    setValidationError('');
    return true;
  };

  if (!visible) {
    return null;
  }

  return (
    <SpaceBetween size="s">
      <div className="commentText">
        <FormField
          label={formLabel}
          description={formDescription}
          errorText={validationError}
          stretch
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Textarea
            disabled={isCreatingComment}
            onChange={onCommentTextChanged}
            value={commentText}
            placeholder={formPlaceholder}
            ariaRequired
          />
        </FormField>
      </div>
      <Box float="right">
        <Button
          variant={buttonVariant || 'normal'}
          loading={isCreatingComment}
          onClick={addComment}
          iconName={buttonIcon}
        >
          {buttonText}
        </Button>
      </Box>
    </SpaceBetween>
  );
};

export default SendCommentForm;
